import { useWorkflowStore } from '@/state/workflow.state';
import type { ToolConfig } from '@/types/tool-config.types';

/**
 * Every tool-config request, in the order it was made: config PATCHes and lock-snapshot releases. The
 * PATCH route replaces the whole config and each node, panel and drawer has its own writer, so two
 * requests in flight at once could land out of order and leave the server on the older snapshot.
 */
let pendingWrites: Promise<unknown> = Promise.resolve();

/** Sends `request` once every request queued before it has settled. */
export function queueToolConfigWrite<T>(request: () => Promise<T>): Promise<T> {
  const write = pendingWrites.then(request);
  pendingWrites = write.catch(() => undefined);
  return write;
}

/** Per workflow, so a write still in flight after switching workflows can't leak into the next one. */
export const writesInFlight = new Map<string, number>();
/** The newest optimistic config, used as the base for edits made before React can publish it. */
export const optimisticConfigs = new Map<string, ToolConfig>();
/**
 * The config the server last accepted, per workflow. A failed write rolls back to this rather than to
 * the state before it, which may itself be an optimistic write that is about to fail too.
 */
export const confirmedConfigs = new Map<string, ToolConfig | null>();
/** Counts config writes per workflow, so a failed one rolls back only if no newer write replaced it. */
export const writeGenerations = new Map<string, number>();
/**
 * The store config this module last set, per workflow with a write in flight. A failed write rolls
 * back only over that, never over a config a reload or a restore put in the store since.
 */
export const ownStoreConfigs = new Map<string, ToolConfig | null>();
/** Core-side changes made while a write was in flight, replayed onto what that write confirms. */
const coreChanges = new Map<string, ((config: ToolConfig | null) => ToolConfig | null)[]>();

/** `config` with every core-side change made during the writes in flight replayed onto it. */
export function withCoreChanges(recipeId: string, config: ToolConfig | null): ToolConfig | null {
  return (coreChanges.get(recipeId) ?? []).reduce((current, change) => change(current), config);
}

/** Drops a workflow's write state once its last write in flight has settled. */
export function clearWriteState(recipeId: string): void {
  writesInFlight.delete(recipeId);
  optimisticConfigs.delete(recipeId);
  confirmedConfigs.delete(recipeId);
  writeGenerations.delete(recipeId);
  ownStoreConfigs.delete(recipeId);
  coreChanges.delete(recipeId);
}

/**
 * Applies a change core made on its own (taking or releasing the lock snapshot) to the store's config
 * and to any write still in flight, so neither that write's success, its next edit nor its rollback
 * can undo it. Each change must be safe to apply twice.
 */
export function applyCoreToolConfigChange(
  recipeId: string,
  change: (config: ToolConfig | null) => ToolConfig | null,
): void {
  const { recipe, setRecipe } = useWorkflowStore.getState();
  const inFlight = writesInFlight.has(recipeId);
  if (recipe.id === recipeId) {
    const toolConfig = change(recipe.toolConfig ?? null);
    // Still this module's only if it was before: a config a reload put in the store stays someone else's.
    const wasOwn = inFlight && recipe.toolConfig === ownStoreConfigs.get(recipeId);
    setRecipe({ ...recipe, toolConfig });
    if (wasOwn) {
      ownStoreConfigs.set(recipeId, toolConfig);
    }
  }
  if (inFlight) {
    coreChanges.set(recipeId, [...(coreChanges.get(recipeId) ?? []), change]);
  }
  const optimistic = optimisticConfigs.get(recipeId);
  if (optimistic) {
    optimisticConfigs.set(recipeId, change(optimistic) ?? optimistic);
  }
  if (confirmedConfigs.has(recipeId)) {
    confirmedConfigs.set(recipeId, change(confirmedConfigs.get(recipeId) ?? null));
  }
}
